import { Button, Container, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import EmptyState from "../components/common/EmptyState";
import LoadingSpinner from "../components/common/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import LicenseTypeModal, {
	type LicenseTypeFormData,
} from "../components/licenseTypes/LicenseTypeModal";
import LicenseTypeOverview from "../components/licenseTypes/LicenseTypeOverview";
import LicenseTypeTable from "../components/licenseTypes/LicenseTypeTable";
import type { AppDispatch, RootState } from "../store/store";
import {
	clearError,
	createLicenseType,
	deleteLicenseType,
	fetchLicenseFeatures,
	fetchLicenseTypes,
	updateLicenseType,
	type LicenseType,
} from "../store/licenseTypes/licenseTypesSlice";

function LicenseTypesPage() {
	const dispatch = useDispatch<AppDispatch>();
	const { licenseTypes, features, loading, error, total } = useSelector(
		(state: RootState) => state.licenseTypes
	);
	const { user: currentUser } = useSelector((state: RootState) => state.auth);

	const [modalOpened, setModalOpened] = useState(false);
	const [editingLicenseType, setEditingLicenseType] =
		useState<LicenseType | null>(null);

	useEffect(() => {
		dispatch(fetchLicenseTypes({ page: 1, limit: 200 }));
		dispatch(fetchLicenseFeatures());
	}, [dispatch]);

	useEffect(() => {
		if (error) {
			notifications.show({
				title: "Error",
				message: error,
				color: "red",
			});
			dispatch(clearError());
		}
	}, [error, dispatch]);

	const openModal = (licenseType: LicenseType | null = null) => {
		setEditingLicenseType(licenseType);
		setModalOpened(true);
	};

	const closeModal = () => {
		setModalOpened(false);
		setEditingLicenseType(null);
	};

	const handleCreateLicenseType = async (values: LicenseTypeFormData) => {
		try {
			await dispatch(createLicenseType(values)).unwrap();
			closeModal();
			notifications.show({
				title: "Success",
				message: "License type created successfully",
				color: "green",
			});
		} catch {
			// Error handled by Redux
		}
	};

	const handleUpdateLicenseType = async (values: LicenseTypeFormData) => {
		if (!editingLicenseType) return;

		try {
			// `key` is immutable server-side, so it is not sent on an update.
			await dispatch(
				updateLicenseType({
					id: editingLicenseType._id,
					name: values.name,
					description: values.description,
					monthlyPrice: values.monthlyPrice,
					features: values.features,
					isActive: values.isActive,
				})
			).unwrap();
			closeModal();
			notifications.show({
				title: "Success",
				message: "License type updated successfully",
				color: "green",
			});
		} catch {
			// Error handled by Redux
		}
	};

	const handleDeleteLicenseType = (licenseType: LicenseType) => {
		modals.openConfirmModal({
			title: "Delete License Type",
			children: (
				<Text>
					Are you sure you want to delete "{licenseType.name}"? Types already in
					use by a license cannot be deleted — withdraw them instead so they stop
					appearing on new licenses.
				</Text>
			),
			labels: { confirm: "Delete", cancel: "Cancel" },
			confirmProps: { color: "red" },
			onConfirm: async () => {
				try {
					await dispatch(deleteLicenseType(licenseType._id)).unwrap();
					notifications.show({
						title: "Success",
						message: "License type deleted successfully",
						color: "green",
					});
				} catch {
					// Error handled by Redux
				}
			},
		});
	};

	const activeCount = licenseTypes.filter(
		(licenseType) => licenseType.isActive
	).length;

	const pageActions = currentUser?.role === "admin" && (
		<Button
			leftSection={<IconPlus size={16} />}
			onClick={() => openModal()}>
			Create License Type
		</Button>
	);

	return (
		<Container size="xl">
			<PageHeader
				title="License Types"
				description="Products you license, and the monthly price each one bills at"
				actions={pageActions}
			/>

			<LicenseTypeOverview
				total={total}
				active={activeCount}
			/>

			{loading ? (
				<LoadingSpinner />
			) : licenseTypes.length === 0 ? (
				<EmptyState
					title="No license types found"
					message="Create a license type to price the licenses you issue."
				/>
			) : (
				<LicenseTypeTable
					licenseTypes={licenseTypes}
					features={features}
					currentUserRole={currentUser?.role}
					onEditLicenseType={openModal}
					onDeleteLicenseType={handleDeleteLicenseType}
				/>
			)}

			<LicenseTypeModal
				opened={modalOpened}
				licenseType={editingLicenseType}
				features={features}
				loading={loading}
				onClose={closeModal}
				onSubmit={
					editingLicenseType ? handleUpdateLicenseType : handleCreateLicenseType
				}
			/>
		</Container>
	);
}

export default LicenseTypesPage;
